<template>
  <div
    class="relative flex h-full flex-row rounded-md bg-gray-50 dark:bg-primary-950"
  >
    <ConfirmModal ref="cancelConfirmModal" />
    <div class="flex min-h-0 min-w-0 flex-1 flex-col">
      <LoadingComponent v-if="loading" />
      <div
        v-else-if="commissionNote"
        class="box flex h-full flex-row divide-x divide-slate-200 overflow-hidden shadow-xl max-md:flex-col max-md:divide-x-0 max-md:divide-y max-md:overflow-y-auto"
      >
        <CommissionNoteSidePanel :commission-note="commissionNote" />

        <!--region lines-->
        <div
          class="flex h-full max-h-full min-h-0 min-w-0 flex-1 flex-col max-md:h-auto max-md:max-h-none max-md:flex-none"
        >
          <div
            class="mx-3 flex shrink-0 justify-between gap-x-2 border-b py-3 print:hidden"
          >
            <div class="flex flex-row items-center justify-start gap-x-2">
              <BackButton
                include-title
                :skip-routes="[
                  'commission_note_show_route',
                  'commission_note_edit_route',
                  'commission_note_print_route'
                ]"
                size="small"
                :fallback-route="{ name: 'commission_note_index_route' }"
              />
            </div>
            <div class="flex flex-row items-center justify-end gap-x-2">
              <template v-if="canEditLines">
                <BaseButton
                  icon="file-invoice"
                  size="small"
                  :title="$t('button.add_documents')"
                  @click="$refs.itemsTable.openAdd('documents')"
                />
                <BaseButton
                  icon="plus"
                  size="small"
                  :title="$t('button.add_line')"
                  @click="$refs.itemsTable.openAdd('free')"
                />
              </template>
              <BaseButton
                icon="print"
                size="small"
                :title="$t('button.print')"
                @click="goToPrint"
              />
              <BaseButton
                v-if="canEditLines"
                color="success"
                icon="check"
                size="small"
                :display-loader="paying"
                :disabled="paying || cancelling || deleting"
                :title="$t('button.mark_as_paid')"
                @click="pay"
              />
              <BaseDropDownMenu
                data-test="actions-menu"
                :options="actionOptions"
                :title="$t('global.action')"
                :disabled="paying || cancelling || deleting"
              />
            </div>
          </div>
          <div
            class="bg-surface-raised min-h-0 flex-1 overflow-y-auto p-3 max-md:flex-none max-md:overflow-visible"
          >
            <CommissionNoteItemsTable
              ref="itemsTable"
              :commission-note="commissionNote"
              :editable="canEditLines"
              :can-see-invoice="canSeeInvoice"
              :can-see-item-settings="canSeeItemSettings"
              @update:commission-note="setCommissionNote"
            />
          </div>
        </div>
        <!--endregion-->
      </div>
      <CommissionNoteLoadError
        v-else-if="loadFailed"
        @retry="fetchCommissionNote"
      />
    </div>
    <BaseSlideOverModal v-model:is-modal-open="activityOpen" width="4xl">
      <template #body>
        <LoadingComponent v-if="loadingActivities" />
        <ActivityContainer v-else :activities="activities" />
      </template>
    </BaseSlideOverModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import ActivityContainer from '@c/Activity/ActivityContainer.vue'
import CommissionNoteItemsTable from '@/views/invoicing/show/commissionNote/components/CommissionNoteItemsTable.vue'
import CommissionNoteSidePanel from '@/views/invoicing/show/commissionNote/components/CommissionNoteSidePanel.vue'
import CommissionNoteLoadError from '@/views/invoicing/show/commissionNote/components/CommissionNoteLoadError.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import BackButton from '@c/addf-package/components/BaseButton/BackButton.vue'

export default {
  name: 'TheCommissionNoteShowPage',
  components: {
    CommissionNoteItemsTable,
    ConfirmModal,
    LoadingComponent,
    ActivityContainer,
    BaseSlideOverModal,
    BackButton,
    BaseButton,
    BaseDropDownMenu,
    CommissionNoteLoadError,
    CommissionNoteSidePanel
  },
  data() {
    return {
      loading: false,
      loadFailed: false,
      paying: false,
      cancelling: false,
      deleting: false,
      commissionNote: null,
      activities: [],
      activityOpen: false,
      loadingActivities: false
    }
  },
  computed: {
    // the other actions of an open note (edit, cancel, delete) and its history; paying has its own button
    actionOptions() {
      const open = this.canWrite && this.commissionNote?.is_open

      return [
        [
          {
            label: this.$t('button.edit'),
            icon: 'pen',
            gate: open,
            action: () => this.goToEdit()
          },
          {
            label: this.$t('global.activity_log'),
            icon: 'clock-rotate-left',
            gate: true,
            action: () => this.openActivities()
          }
        ],
        [
          {
            label: this.$t('button.cancel_commission_note'),
            icon: 'ban',
            icon_color: 'text-red-600',
            gate: open,
            action: () => this.cancelNote()
          },
          {
            label: this.$t('button.delete_commission_note'),
            icon: 'trash',
            icon_color: 'text-red-600',
            gate: open,
            action: () => this.deleteNote()
          }
        ]
      ]
    },
    canWrite() {
      return (
        this.$can('manage_commission_notes') ||
        this.$can('manage_commission_settings')
      )
    },
    // the lines of an open note are changed right on the page, as the lines of an invoice
    canEditLines() {
      return this.canWrite && !!this.commissionNote?.is_open
    },
    canSeeInvoice() {
      return this.$can('see_invoicing')
    },
    canSeeItemSettings() {
      return this.$can('see_content')
    }
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        // leaving the page empties the param before the component is unmounted
        if (!id) return
        this.fetchCommissionNote()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      processFetchAction: 'commissionNote/fetchCommissionNote',
      processFetchActivitiesAction:
        'commissionNote/fetchCommissionNoteActivities',
      processPayAction: 'commissionNote/payCommissionNote',
      processCancelAction: 'commissionNote/cancelCommissionNote',
      processDeleteAction: 'commissionNote/deleteCommissionNote'
    }),
    goToPrint() {
      this.$router.push({
        name: 'commission_note_print_route',
        params: { id: this.commissionNote.id }
      })
    },
    goToEdit() {
      this.$router.push({
        name: 'commission_note_edit_route',
        params: { id: this.commissionNote.id }
      })
    },
    setCommissionNote(commissionNote) {
      this.commissionNote = commissionNote
    },
    fetchCommissionNote() {
      this.loading = true
      this.loadFailed = false
      const targetId = this.$route.params.id
      this.processFetchAction({ id: targetId })
        .then((response) => {
          if (this.$route.params.id !== targetId) return
          this.commissionNote = response.commission_note
        })
        .catch((error) => {
          if (this.$route.params.id !== targetId) return
          // a missing or forbidden note leaves the page; any other failure shows a message and a way to try again
          if ([403, 404].includes(error?.response?.status)) {
            this.$router.push({ name: 'commission_note_index_route' })
            return
          }

          this.commissionNote = null
          this.loadFailed = true
        })
        .finally(() => {
          if (this.$route.params.id === targetId) this.loading = false
        })
    },
    openActivities() {
      this.activityOpen = true
      this.loadingActivities = true
      this.processFetchActivitiesAction({ id: this.$route.params.id })
        .then((response) => {
          this.activities = response.activities ?? []
        })
        .catch(() => {
          this.activities = []
        })
        .finally(() => {
          this.loadingActivities = false
        })
    },
    async cancelNote() {
      const confirmed = await this.$refs.cancelConfirmModal.show({
        title: this.$t('global.cancel_commission_note_title'),
        message: this.$t('global.cancel_commission_note_message'),
        okButton: this.$t('button.cancel_commission_note')
      })

      if (!confirmed) {
        return
      }

      this.cancelling = true
      this.processCancelAction({ id: this.commissionNote.id })
        .then(() => this.fetchCommissionNote())
        .catch(() => {})
        .finally(() => {
          this.cancelling = false
        })
    },
    async deleteNote() {
      const confirmed = await this.$refs.cancelConfirmModal.show({
        title: this.$t('button.delete_commission_note'),
        message: this.$t('global.commission_note_delete_confirm'),
        okButton: this.$t('button.delete_commission_note')
      })

      if (!confirmed) {
        return
      }

      this.deleting = true
      this.processDeleteAction({ id: this.commissionNote.id })
        .then(() => {
          this.$h.toastSuccess(this.$t('toasts.commission_note_deleted'))
          this.$router.push({ name: 'commission_note_index_route' })
        })
        .catch(() => {})
        .finally(() => {
          this.deleting = false
        })
    },
    // paying freezes the note: it asks first, as cancelling and deleting do
    async pay() {
      const confirmed = await this.$refs.cancelConfirmModal.show({
        title: this.$t('global.pay_commission_note_title'),
        message: this.$t('global.pay_commission_note_message'),
        okButton: this.$t('button.mark_as_paid')
      })

      if (!confirmed) {
        return
      }

      this.paying = true
      this.processPayAction({ id: this.commissionNote.id })
        .then(() => this.fetchCommissionNote())
        .catch(() => {})
        .finally(() => {
          this.paying = false
        })
    }
  }
}
</script>
